import Vue from 'vue';

import { MatrixFormatError } from '@/models/matrix-format-error';
import { MatrixProcess } from '@/models/matrix-process';
import { MatrixProperties, MatrixStatus } from '@/modules/matrix/definitions';
import { MatrixStatusType } from '@/models/matrix-status-enum';

function initialState(): MatrixProperties {
  return {
    elements: [],
    isLoading: false
  };
}

export const matrix = Vue.observable<MatrixProperties>(initialState());

export function isDownloadable(index?: number): boolean {
  if (index != undefined) {
    const element = matrix.elements[index];
    return element?.report.length > 0;
  }
  return matrix.elements.some((_, elementIndex) => isDownloadable(elementIndex));
}

export function hasStatusMessage(index: number): boolean {
  const element = matrix.elements[index];
  if (!element) {
    return false;
  }

  const { status } = element;
  if (!status) {
    return false;
  }

  return status.message != null && status.message.length > 0;
}

export function getMatrixCount(): number {
  return matrix.elements.length;
}

export function getStatusColor(index: number): string {
  const element = matrix.elements[index];
  if (!element) {
    throw new Error(`No matrix element found at index ${index}`);
  }

  switch (element.status.type) {
    case MatrixStatusType.initialized:
    case MatrixStatusType.processing:
      return 'primary';
    case MatrixStatusType.complete:
      return 'success';
    case MatrixStatusType.completeWithAlerts:
      return 'warning';
    case MatrixStatusType.error:
    case MatrixStatusType.completeWithRejection:
    case MatrixStatusType.uncomplete:
      return 'error';
  }
}

export function setFiles(files: File[]): void {
  const matrixElements = files.map(file => {
    return { file, report: [], status: { type: MatrixStatusType.initialized } };
  });
  Vue.set(matrix, 'elements', matrixElements);
}

export function setStatus(index: number, status: MatrixStatus): void {
  Vue.set(matrix.elements[index], 'status', status);
}

export function setCurrentProcess(process: MatrixProcess): void {
  Vue.set(matrix, 'currentProcess', process);
}

export function toggleLoading(): void {
  matrix.isLoading = !matrix.isLoading;
}

export function addErrorToReport(index: number, error: MatrixFormatError): void {
  Vue.set(matrix.elements[index], 'report', Object.freeze([...matrix.elements[index].report, error]));
}

export function clearReports(): void {
  matrix.elements.forEach((_, index) => {
    Vue.set(matrix.elements[index], 'report', []);
    Vue.set(matrix.elements[index], 'status', { type: MatrixStatusType.initialized });
  });
}

export function reset(): void {
  const state = initialState();
  delete matrix.currentProcess;
  (Object.keys(state) as (keyof MatrixProperties)[]).forEach(key => {
    Vue.set(matrix, key, state[key]);
  });
}
